/**
 * Bench Ergonomics Calculator — Löylyns lag (Löyly's Law)
 *
 * Calculates sauna bench (lav) Y-coordinates top-down from the ceiling
 * and validates that the bather's feet stay above the heater's top stones.
 *
 * All thresholds are configurable via the Rule Registry.
 */
import { getRuleValue, type RuleOverrides } from '../../data/ruleRegistry';
import type { Market } from '../location/types';
import { getT } from '../../i18n';

// ─── Types ──────────────────────────────────────────────────

export interface ErgonomicsInput {
    /** Interior ceiling height in millimetres (standard ≈ 2100) */
    ceilingHeightMm: number;
    /** Height of the heater's topmost stones in mm from floor (typical ≈ 600) */
    heaterStoneHeightMm?: number;
}

export interface ErgonomicsResult {
    /** Upper bench Y-position from floor in mm (Finnish: top-down) */
    upperBenchYMm: number;
    /** Lower bench (foot bench) Y-position from floor in mm (Finnish: top-down) */
    lowerBenchYMm: number;
    /** Swedish standard: upper bench Y from floor in mm (bottom-up) */
    swedishUpperBenchYMm: number;
    /** Swedish standard: lower bench Y from floor in mm (bottom-up) */
    swedishLowerBenchYMm: number;
    /** Whether the configuration passes Finnish Löyly compliance */
    isLoylyCompliant: boolean;
    /** Advisory text */
    advice: string;
    /** Warning text if non-compliant (null if OK) */
    warning: string | null;
    /** Minimum ceiling height in mm needed for compliance */
    minCeilingMm: number;
}

// ─── Public API ─────────────────────────────────────────────

export function calculateBenchErgonomics(
    input: ErgonomicsInput,
    overrides: RuleOverrides = {},
    market: Market = 'se',
): ErgonomicsResult {
    const t = getT(market);
    const upperOffset = getRuleValue<number>('ergonomics.upperBenchOffsetMm', overrides);
    const benchGap = getRuleValue<number>('ergonomics.benchGapMm', overrides);
    const defaultStoneHeight = getRuleValue<number>('ergonomics.defaultStoneHeightMm', overrides);

    // Swedish bottom-up model
    const swedishLower = getRuleValue<number>('ergonomics.swedishLowerBenchFromFloorMm', overrides);
    const swedishStep = getRuleValue<number>('ergonomics.swedishBenchStepMm', overrides);

    const stoneHeight = input.heaterStoneHeightMm ?? defaultStoneHeight;
    const ceilingH = input.ceilingHeightMm;

    // 1. Finnish: Upper bench top-down from ceiling
    const upperBenchY = ceilingH - upperOffset;

    // 2. Finnish: Lower bench (foot bench) gap below upper
    const lowerBenchY = upperBenchY - benchGap;

    // 3. Swedish: bottom-up from floor
    const swedishLowerBenchY = swedishLower;
    const swedishUpperBenchY = swedishLower + swedishStep;

    // 4. Löyly validation: feet must be ≥ stone height
    const isCompliant = lowerBenchY >= stoneHeight;

    // 5. Minimum ceiling for compliance
    const minCeiling = stoneHeight + benchGap + upperOffset;

    const advice = isCompliant
        ? t.domain_ergo_adviceOk
        : t.domain_ergo_adviceFail;

    const warning = isCompliant
        ? null
        : t.domain_ergo_warningFail;

    return {
        upperBenchYMm: upperBenchY,
        lowerBenchYMm: lowerBenchY,
        swedishUpperBenchYMm: swedishUpperBenchY,
        swedishLowerBenchYMm: swedishLowerBenchY,
        isLoylyCompliant: isCompliant,
        advice,
        warning,
        minCeilingMm: minCeiling,
    };
}
